import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Search, Package, FolderOpen, ChevronRight, Upload } from 'lucide-react';

export default function ProductsPage() {
  const { API } = useAuth();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState('');
  const [filterCat, setFilterCat] = useState('all');
  const [filterSub, setFilterSub] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [catDialogOpen, setCatDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', category: '', subcategory: '', price: '', description: '', stock: '' });
  const [catForm, setCatForm] = useState({ name: '', parent_id: '' });
  const [editingCat, setEditingCat] = useState(null);
  const [editCatName, setEditCatName] = useState('');

  const fetchAll = useCallback(() => {
    Promise.all([
      axios.get(`${API}/products`),
      axios.get(`${API}/product-categories`)
    ]).then(([p, c]) => { setProducts(p.data); setCategories(c.data); }).catch(() => toast.error('Error al cargar'));
  }, [API]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const topCats = categories.filter(c => c.type === 'category');
  const getSubcats = (parentId) => categories.filter(c => c.parent_id === parentId);
  const selectedCatSubs = filterCat !== 'all' ? getSubcats(filterCat) : [];

  const getCatName = (catId) => categories.find(c => c.id === catId)?.name || catId || '-';

  const openCreate = () => {
    setEditing(null);
    setForm({ name: '', category: '', subcategory: '', price: '', description: '', stock: '' });
    setDialogOpen(true);
  };
  const openEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name, category: p.category || '', subcategory: p.subcategory || '',
      price: String(p.price), description: p.description || '',
      stock: p.stock !== null && p.stock !== undefined ? String(p.stock) : ''
    });
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!form.name || !form.price || !form.category) { toast.error('Nombre, categoria y precio son requeridos'); return; }
    try {
      const payload = { ...form, price: parseFloat(form.price), stock: form.stock !== '' ? parseInt(form.stock) : null };
      if (editing) {
        await axios.put(`${API}/products/${editing.id}`, payload);
        toast.success('Producto actualizado');
      } else {
        await axios.post(`${API}/products`, payload);
        toast.success('Producto creado');
      }
      setDialogOpen(false);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminar este producto?')) return;
    try { await axios.delete(`${API}/products/${id}`); toast.success('Eliminado'); fetchAll(); } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const toggleActive = async (p) => {
    try { await axios.put(`${API}/products/${p.id}`, { active: !p.active }); fetchAll(); } catch { toast.error('Error'); }
  };

  const handleAddCategory = async () => {
    if (!catForm.name.trim()) { toast.error('Nombre requerido'); return; }
    try {
      await axios.post(`${API}/product-categories`, {
        name: catForm.name.trim(),
        parent_id: catForm.parent_id || null
      });
      toast.success(catForm.parent_id ? 'Subcategoria creada' : 'Categoria creada');
      setCatForm({ name: '', parent_id: '' });
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const handleDeleteCategory = async (id) => {
    if (!window.confirm('Eliminar esta categoria?')) return;
    try { await axios.delete(`${API}/product-categories/${id}`); toast.success('Eliminada'); fetchAll(); } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const handleRenameCategory = async () => {
    if (!editingCat || !editCatName.trim()) return;
    try {
      await axios.put(`${API}/product-categories/${editingCat}`, { name: editCatName.trim() });
      toast.success('Nombre actualizado');
      setEditingCat(null);
      setEditCatName('');
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const formSubcats = form.category ? categories.filter(c => c.parent_id === form.category) : [];

  const filtered = products.filter(p => {
    if (filterCat !== 'all' && p.category !== filterCat) return false;
    if (filterSub !== 'all' && p.subcategory !== filterSub) return false;
    if (search && !p.name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  return (
    <div className="space-y-6 animate-fade-in" data-testid="products-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-outfit text-2xl sm:text-3xl font-semibold text-stone-900 tracking-tight">Productos</h1>
          <p className="text-stone-500 text-sm mt-1">{products.length} productos en {topCats.length} categorias</p>
        </div>
        <div className="flex gap-2">
          <Button data-testid="manage-categories-btn" onClick={() => setCatDialogOpen(true)} variant="outline" className="rounded-full border-stone-300">
            <FolderOpen className="w-4 h-4 mr-2" /> Categorias
          </Button>
          <Button data-testid="add-product-btn" onClick={openCreate} className="bg-orange-700 hover:bg-orange-800 text-white rounded-full px-6 shadow-sm hover:shadow-md active:scale-[0.98] transition-all">
            <Plus className="w-4 h-4 mr-2" /> Nuevo Producto
          </Button>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
          <Input data-testid="product-search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar producto..." className="pl-10 rounded-xl border-stone-300" />
        </div>
        <Select value={filterCat} onValueChange={v => { setFilterCat(v); setFilterSub('all'); }}>
          <SelectTrigger data-testid="product-filter-category" className="w-full sm:w-48 rounded-xl border-stone-300"><SelectValue placeholder="Categoria" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas las categorias</SelectItem>
            {topCats.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
        {selectedCatSubs.length > 0 && (
          <Select value={filterSub} onValueChange={setFilterSub}>
            <SelectTrigger data-testid="product-filter-subcategory" className="w-full sm:w-48 rounded-xl border-stone-300"><SelectValue placeholder="Subcategoria" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas</SelectItem>
              {selectedCatSubs.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </div>

      <Card className="bg-white border border-stone-200 shadow-sm rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="products-table">
            <thead>
              <tr className="bg-stone-50/50">
                <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider text-stone-500">Producto</th>
                <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider text-stone-500">Categoria</th>
                <th className="text-right p-4 text-xs font-semibold uppercase tracking-wider text-stone-500">Precio</th>
                <th className="text-center p-4 text-xs font-semibold uppercase tracking-wider text-stone-500">Stock</th>
                <th className="text-center p-4 text-xs font-semibold uppercase tracking-wider text-stone-500">Estado</th>
                <th className="text-right p-4 text-xs font-semibold uppercase tracking-wider text-stone-500">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(p => (
                <tr key={p.id} className="border-t border-stone-100 hover:bg-stone-50/50 transition-colors">
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 bg-stone-100 rounded-lg flex items-center justify-center"><Package className="w-4 h-4 text-stone-500" strokeWidth={1.5} /></div>
                      <div>
                        <p className="text-sm font-medium text-stone-900">{p.name}</p>
                        {p.description && <p className="text-xs text-stone-500">{p.description}</p>}
                      </div>
                    </div>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center gap-1 text-xs text-stone-600">
                      <span className="font-medium">{getCatName(p.category)}</span>
                      {p.subcategory && <><ChevronRight className="w-3 h-3 text-stone-400" /><span>{getCatName(p.subcategory)}</span></>}
                    </div>
                  </td>
                  <td className="p-4 text-right text-sm font-semibold text-stone-900">${p.price.toFixed(2)}</td>
                  <td className="p-4 text-center">
                    {p.stock === null || p.stock === undefined ? (
                      <span className="text-xs text-stone-400">Ilimitado</span>
                    ) : p.stock <= 10 && p.stock >= 0 ? (
                      <span className="text-xs font-medium px-2.5 py-0.5 rounded-full bg-red-100 text-red-800">{p.stock}</span>
                    ) : (
                      <span className="text-xs font-medium px-2.5 py-0.5 rounded-full bg-green-100 text-green-800">{p.stock}</span>
                    )}
                  </td>
                  <td className="p-4 text-center">
                    <button data-testid={`toggle-active-${p.id}`} onClick={() => toggleActive(p)} className={`text-xs font-medium px-3 py-1 rounded-full transition-colors ${p.active ? 'bg-green-100 text-green-800 hover:bg-green-200' : 'bg-red-100 text-red-800 hover:bg-red-200'}`}>
                      {p.active ? 'Activo' : 'Inactivo'}
                    </button>
                  </td>
                  <td className="p-4 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button data-testid={`edit-product-${p.id}`} onClick={() => openEdit(p)} className="p-2 rounded-lg hover:bg-stone-100 text-stone-500 hover:text-orange-700 transition-colors"><Pencil className="w-4 h-4" strokeWidth={1.5} /></button>
                      <button data-testid={`delete-product-${p.id}`} onClick={() => handleDelete(p.id)} className="p-2 rounded-lg hover:bg-red-50 text-stone-500 hover:text-red-700 transition-colors"><Trash2 className="w-4 h-4" strokeWidth={1.5} /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-stone-400 text-sm">No hay productos</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Product Form Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle className="font-outfit">{editing ? 'Editar Producto' : 'Nuevo Producto'}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div><Label>Nombre</Label><Input data-testid="product-form-name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="rounded-xl mt-1" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Categoria</Label>
                <Select value={form.category} onValueChange={v => setForm({ ...form, category: v, subcategory: '' })}>
                  <SelectTrigger data-testid="product-form-category" className="rounded-xl mt-1"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                  <SelectContent>{topCats.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Subcategoria</Label>
                <Select value={form.subcategory} onValueChange={v => setForm({ ...form, subcategory: v })}>
                  <SelectTrigger data-testid="product-form-subcategory" className="rounded-xl mt-1"><SelectValue placeholder={formSubcats.length ? 'Seleccionar' : 'Sin subcategorias'} /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Ninguna</SelectItem>
                    {formSubcats.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Precio</Label><Input data-testid="product-form-price" type="number" step="0.01" value={form.price} onChange={e => setForm({ ...form, price: e.target.value })} className="rounded-xl mt-1" /></div>
              <div><Label>Stock (vacio = ilimitado)</Label><Input data-testid="product-form-stock" type="number" min="0" value={form.stock} onChange={e => setForm({ ...form, stock: e.target.value })} placeholder="Ilimitado" className="rounded-xl mt-1" /></div>
            </div>
            <div><Label>Descripcion</Label><Input data-testid="product-form-desc" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} className="rounded-xl mt-1" /></div>
            {editing && (
              <div>
                <Label>Imagen del producto</Label>
                <div className="mt-1">
                  <label className="flex items-center gap-2 px-4 py-3 bg-stone-50 border border-dashed border-stone-300 rounded-xl cursor-pointer hover:bg-stone-100 transition-colors">
                    <Upload className="w-4 h-4 text-stone-500" />
                    <span className="text-sm text-stone-600">Subir imagen (max 5MB)</span>
                    <input data-testid="product-image-upload" type="file" accept="image/*" className="hidden" onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      const fd = new FormData();
                      fd.append('file', file);
                      try {
                        await axios.post(`${API}/products/${editing.id}/image`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                        toast.success('Imagen subida');
                        fetchAll();
                      } catch (err) { toast.error(err.response?.data?.detail || 'Error al subir'); }
                    }} />
                  </label>
                  {editing.image_path && <p className="text-xs text-green-600 mt-1">Imagen actual cargada</p>}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-full">Cancelar</Button>
            <Button data-testid="product-form-submit" onClick={handleSubmit} className="bg-orange-700 hover:bg-orange-800 text-white rounded-full">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Category Management Dialog */}
      <Dialog open={catDialogOpen} onOpenChange={setCatDialogOpen}>
        <DialogContent className="rounded-2xl max-w-lg">
          <DialogHeader><DialogTitle className="font-outfit">Gestionar Categorias</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="flex gap-2">
              <Input data-testid="cat-name-input" value={catForm.name} onChange={e => setCatForm({ ...catForm, name: e.target.value })} placeholder="Nombre de categoria..." className="rounded-xl flex-1" />
              <Select value={catForm.parent_id || 'none'} onValueChange={v => setCatForm({ ...catForm, parent_id: v === 'none' ? '' : v })}>
                <SelectTrigger data-testid="cat-parent-select" className="rounded-xl w-40"><SelectValue placeholder="Tipo" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Categoria</SelectItem>
                  {topCats.map(c => <SelectItem key={c.id} value={c.id}>Sub de: {c.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button data-testid="cat-add-btn" onClick={handleAddCategory} className="bg-orange-700 hover:bg-orange-800 text-white rounded-xl"><Plus className="w-4 h-4" /></Button>
            </div>

            <div className="space-y-2 max-h-72 overflow-y-auto">
              {topCats.map(cat => (
                <div key={cat.id}>
                  <div className="flex items-center justify-between bg-stone-50 rounded-xl p-3">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <FolderOpen className="w-4 h-4 text-orange-600 flex-shrink-0" strokeWidth={1.5} />
                      {editingCat === cat.id ? (
                        <div className="flex items-center gap-1 flex-1">
                          <Input data-testid={`rename-cat-input-${cat.id}`} value={editCatName} onChange={e => setEditCatName(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && handleRenameCategory()}
                            className="rounded-lg h-7 text-sm flex-1" autoFocus />
                          <button onClick={handleRenameCategory} className="text-xs text-green-700 font-medium px-2 py-1 hover:bg-green-50 rounded">OK</button>
                          <button onClick={() => setEditingCat(null)} className="text-xs text-stone-500 px-1 py-1 hover:bg-stone-100 rounded">X</button>
                        </div>
                      ) : (
                        <>
                          <span className="text-sm font-medium text-stone-900 truncate">{cat.name}</span>
                          <Badge className="bg-stone-200 text-stone-600 border-0 text-xs rounded-full">{getSubcats(cat.id).length} sub</Badge>
                        </>
                      )}
                    </div>
                    {editingCat !== cat.id && (
                      <div className="flex items-center gap-0.5 flex-shrink-0">
                        <button data-testid={`rename-cat-${cat.id}`} onClick={() => { setEditingCat(cat.id); setEditCatName(cat.name); }} className="p-1 rounded hover:bg-stone-200 text-stone-400 hover:text-orange-700"><Pencil className="w-3.5 h-3.5" /></button>
                        <button data-testid={`delete-cat-${cat.id}`} onClick={() => handleDeleteCategory(cat.id)} className="p-1 rounded hover:bg-red-100 text-stone-400 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    )}
                  </div>
                  {getSubcats(cat.id).map(sub => (
                    <div key={sub.id} className="flex items-center justify-between ml-6 mt-1 bg-stone-50/50 rounded-lg p-2.5">
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <ChevronRight className="w-3 h-3 text-stone-400 flex-shrink-0" />
                        {editingCat === sub.id ? (
                          <div className="flex items-center gap-1 flex-1">
                            <Input data-testid={`rename-cat-input-${sub.id}`} value={editCatName} onChange={e => setEditCatName(e.target.value)}
                              onKeyDown={e => e.key === 'Enter' && handleRenameCategory()}
                              className="rounded-lg h-7 text-sm flex-1" autoFocus />
                            <button onClick={handleRenameCategory} className="text-xs text-green-700 font-medium px-2 py-1 hover:bg-green-50 rounded">OK</button>
                            <button onClick={() => setEditingCat(null)} className="text-xs text-stone-500 px-1 py-1 hover:bg-stone-100 rounded">X</button>
                          </div>
                        ) : (
                          <span className="text-sm text-stone-700 truncate">{sub.name}</span>
                        )}
                      </div>
                      {editingCat !== sub.id && (
                        <div className="flex items-center gap-0.5 flex-shrink-0">
                          <button data-testid={`rename-cat-${sub.id}`} onClick={() => { setEditingCat(sub.id); setEditCatName(sub.name); }} className="p-1 rounded hover:bg-stone-200 text-stone-400 hover:text-orange-700"><Pencil className="w-3 h-3" /></button>
                          <button data-testid={`delete-cat-${sub.id}`} onClick={() => handleDeleteCategory(sub.id)} className="p-1 rounded hover:bg-red-100 text-stone-400 hover:text-red-600"><Trash2 className="w-3 h-3" /></button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ))}
              {topCats.length === 0 && <p className="text-sm text-stone-400 text-center py-4">No hay categorias. Crea la primera.</p>}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCatDialogOpen(false)} className="rounded-full">Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
